import { minimatch } from "minimatch";
import type { GitClient } from "../git/GitClient.js";
import type { StoredPatch } from "../types.js";

/**
 * Inputs for one ownership resolution call.
 *
 * `originalFileName` is the pre-rename path used when the generator moved
 * the file between generations — tree lookups at ancestor generations need
 * the original path because that's where the generator produced the content.
 *
 * `warnedGens` and `warnings` are mutable accumulators threaded across calls
 * within a single replay run. The canonical path appends a one-time warning
 * when a base generation tree is unreachable (shallow clone / aggressive GC)
 * so the customer sees a single message per affected base, not one per file.
 */
export interface OwnershipContext {
    readonly file: string;
    readonly originalFileName?: string;
    readonly patch: StoredPatch;
    readonly currentGenSha: string;
    readonly fernignorePatterns: readonly string[];
    readonly warnedGens: Set<string>;
    readonly warnings: string[];
}

/**
 * Single canonical home for "is this file user-owned?" resolution. Every
 * service-level check (preGenerationRebase, rebasePatches, trimAbsorbedFiles)
 * routes through `resolveCanonical`.
 *
 * The earlier `rebasePatches` inline variant (`resolveAtCurrentGen`) was
 * collapsed under FER-10386 once pre-flight investigation showed its
 * narrower fall-back was harmless on every reachable scenario but worse
 * on the unreachable-base-tree case. See `docs/known-divergences.md`
 * entry 5 for the closed-out divergence record.
 */
export class FileOwnership {
    constructor(private readonly git: GitClient) {}

    /**
     * Canonical resolution. Returns true if `file` is user-owned per:
     *
     *   1. `patch.user_owned === true` — authoritative, persisted flag.
     *   2. `file === ".fernignore"` — always user-owned.
     *   3. File matches a `.fernignore` pattern (via minimatch).
     *   4. Patch content shows `--- /dev/null` for this file (legacy-safe
     *      signal for patches predating `user_owned` or whose flag was lost).
     *   5. Tree-based check against `patch.base_generation` when reachable
     *      AND distinct from `currentGenSha`. Unreachable base → conservative
     *      user-owned (silent-absorption safety net), with a one-time warning
     *      per base SHA.
     *   6. Final fallback: check `currentGenSha` when no usable base exists
     *      (legacy one-gen lockfile).
     */
    async resolveCanonical(ctx: OwnershipContext): Promise<boolean> {
        const { file, originalFileName, patch, currentGenSha, fernignorePatterns, warnedGens, warnings } = ctx;
        if (patch.user_owned) return true;
        if (file === ".fernignore") return true;
        if (fernignorePatterns.some((p) => file === p || minimatch(file, p))) return true;

        if (fileIsCreationInPatchContent(patch.patch_content, originalFileName ?? file)) {
            return true;
        }

        const base = patch.base_generation;
        if (base && base !== currentGenSha) {
            const reachable =
                (await this.git.commitExists(base)) || (await this.git.treeExists(base));
            if (!reachable) {
                if (!warnedGens.has(base)) {
                    warnedGens.add(base);
                    warnings.push(
                        `Base generation ${base.slice(0, 7)} is unreachable (shallow clone or pruned history). ` +
                            `Treating affected files as user-owned to avoid silent data loss. ` +
                            `Run \`git fetch --unshallow\` or avoid \`git gc --prune\` to restore full history.`
                    );
                }
                return true;
            }
            return (await this.git.showFile(base, originalFileName ?? file)) === null;
        }

        return (await this.git.showFile(currentGenSha, originalFileName ?? file)) === null;
    }
}

/**
 * Returns true if `file`'s diff section in `patchContent` starts with
 * `--- /dev/null`, meaning the patch introduces this file (user creation).
 *
 * Pure parser — no git access. Used as a legacy-safe fallback when
 * `patch.user_owned` is absent or cleared.
 */
export function fileIsCreationInPatchContent(patchContent: string, file: string): boolean {
    const lines = patchContent.split("\n");
    let inFileSection = false;
    for (const line of lines) {
        if (line.startsWith("diff --git ")) {
            inFileSection = line.includes(` b/${file}`) || line.endsWith(` b/${file}`);
            continue;
        }
        if (inFileSection && line.startsWith("--- ")) {
            return line === "--- /dev/null";
        }
    }
    return false;
}
